import React, { useState, useEffect } from 'react';
import './BufferIntegration.css';

export default function BufferIntegration() {
  const [bufferConnected, setBufferConnected] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState({});
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    checkBufferStatus();
  }, []);

  const checkBufferStatus = async () => {
    try {
      const response = await fetch('/api/buffer/status');
      const data = await response.json();
      setBufferConnected(data.connected);
      
      if (data.connected) {
        fetchProfiles();
        fetchScheduledPosts();
      }
    } catch (err) {
      console.error('Error checking Buffer status:', err);
    }
  };

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/buffer/profiles');
      const data = await response.json();
      setProfiles(data.profiles || []);
      
      // Initialize selected profiles
      const selected = {};
      data.profiles?.forEach(p => {
        selected[p.id] = true;
      });
      setSelectedProfiles(selected);
    } catch (err) {
      console.error('Error fetching Buffer profiles:', err);
      setError('Failed to fetch Buffer profiles');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledPosts = async () => {
    try {
      const response = await fetch('/api/buffer/scheduled');
      const data = await response.json();
      setScheduledPosts(data.scheduled || []);
    } catch (err) {
      console.error('Error fetching scheduled posts:', err);
    }
  };

  const handleAuthorize = async () => {
    try {
      const response = await fetch('/api/buffer/authorize', { method: 'POST' });
      const data = await response.json();
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err) {
      setError('Failed to authorize Buffer');
    }
  };

  const pushToBuffer = async (postId, scheduleTime) => {
    setLoading(true);
    try {
      const selectedProfileIds = Object.keys(selectedProfiles)
        .filter(id => selectedProfiles[id]);

      if (selectedProfileIds.length === 0) {
        setError('Please select at least one social profile');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/buffer/push-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          postId,
          profileIds: selectedProfileIds,
          scheduleTime
        })
      });

      const data = await response.json();
      if (response.ok) {
        alert('Post pushed to Buffer successfully!');
        fetchScheduledPosts();
      } else {
        setError(data.error || 'Failed to push to Buffer');
      }
    } catch (err) {
      setError('Error pushing post to Buffer');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelBufferPost = async (bufferId) => {
    if (!window.confirm('Cancel this scheduled post?')) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/buffer/cancel/${bufferId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Post cancelled');
        fetchScheduledPosts();
      } else {
        setError('Failed to cancel post');
      }
    } catch (err) {
      setError('Error cancelling post');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileToggle = (profileId) => {
    setSelectedProfiles({
      ...selectedProfiles,
      [profileId]: !selectedProfiles[profileId]
    });
  };

  return (
    <div className="buffer-integration">
      <h2>Buffer Integration</h2>

      {error && <div className="error-message">{error}</div>}

      {!bufferConnected ? (
        <div className="buffer-connect">
          <p>Connect Buffer to schedule your posts across all platforms automatically.</p>
          <button onClick={handleAuthorize} className="btn-buffer-auth">
            Connect Buffer
          </button>
        </div>
      ) : (
        <>
          <div className="buffer-profiles">
            <h3>Connected Profiles</h3>
            {loading ? (
              <p>Loading profiles...</p>
            ) : profiles.length > 0 ? (
              <div className="profiles-grid">
                {profiles.map(profile => (
                  <div key={profile.id} className="profile-card">
                    <div className="profile-checkbox">
                      <input
                        type="checkbox"
                        id={`profile-${profile.id}`}
                        checked={selectedProfiles[profile.id] || false}
                        onChange={() => handleProfileToggle(profile.id)}
                      />
                      <label htmlFor={`profile-${profile.id}`}>
                        
                        
                        
                        
                        {' '}{profile.formatted_username}
                      </label>
                    </div>
                    <div className="profile-stats">
                      <span className="followers">{profile.followers?.toLocaleString()} followers</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No connected profiles yet. Authorize Buffer above.</p>
            )}
          </div>

          <div className="scheduled-posts">
            <h3>Scheduled Posts ({scheduledPosts.length})</h3>
            {scheduledPosts.length > 0 ? (
              <div className="posts-list">
                {scheduledPosts.map(post => (
                  <div key={post.id} className="scheduled-post-item">
                    <div className="post-info">
                      <p className="post-text">{post.text?.substring(0, 100)}...</p>
                      <span className="post-time">
                        {new Date(post.scheduled_at * 1000).toLocaleString()}
                      </span>
                    </div>
                    <button
                      onClick={() => cancelBufferPost(post.id)}
                      className="btn-cancel"
                      disabled={loading}
                    >
                      ✕ Cancel
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p>No scheduled posts yet.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
